/**
 * iam-aside — slide-out drawer web component.
 *
 * Usage:
 *   <iam-aside id="MyDrawer" position="right">
 *     <div class="aside__overlay"></div>
 *     <div class="aside__panel">
 *       <div class="aside__header">
 *         <span class="aside__title">Title</span>
 *         <button class="aside__close" aria-label="Close">…</button>
 *       </div>
 *       <div class="aside__body">…</div>
 *     </div>
 *   </iam-aside>
 *
 * Triggers: any element with [data-aside-target="MyDrawer"] toggles the aside on click
 * (opens when closed; closes when open, e.g. search icon ↔ close).
 *
 * Events (iamotaEventHandler):
 *   aside:opened / aside:opened:<id>
 *   aside:closed / aside:closed:<id>
 */

import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import './_aside.scss';
var ASIDE_SCROLL_LOCK_DATA_ATTR = 'data-aside-scroll-lock';
export default class iamAside extends HTMLElement {
  _syncScrollLockState() {
    var hasOpenAside = Boolean(document.querySelector('iam-aside.aside--open'));
    if (hasOpenAside) {
      document.documentElement.setAttribute(ASIDE_SCROLL_LOCK_DATA_ATTR, 'true');
      document.body.setAttribute(ASIDE_SCROLL_LOCK_DATA_ATTR, 'true');
      return;
    }
    document.documentElement.removeAttribute(ASIDE_SCROLL_LOCK_DATA_ATTR);
    document.body.removeAttribute(ASIDE_SCROLL_LOCK_DATA_ATTR);
  }
  connectedCallback() {
    this.panel = this.querySelector('.aside__panel');
    this.overlay = this.querySelector('.aside__overlay');
    this.closeButton = this.querySelector('.aside__close');
    if (!this.panel || !this.overlay || !this.closeButton) {
      console.error('iam-aside is missing required child elements (.aside__panel, .aside__overlay, .aside__close). Initialization aborted.', this);
      return;
    }
    this.handleKeydownRef = this.handleKeydown.bind(this);
    this.closeButton.addEventListener('click', () => this.closeAside());
    this.overlay.addEventListener('click', () => this.closeAside());
    this._bindTriggers();
  }
  disconnectedCallback() {
    this._unbindTriggers();
    document.removeEventListener('keydown', this.handleKeydownRef);
    this._syncScrollLockState();
  }
  _bindTriggers() {
    this.triggers = document.querySelectorAll("[data-aside-target=\"".concat(this.id, "\"]"));
    this.openAsideRefs = new Map();
    this.triggers.forEach(trigger => {
      var toggleRef = event => {
        event.preventDefault();
        if (this.classList.contains('aside--open')) {
          this.closeAside(trigger);
        } else {
          this.openAside(trigger);
        }
      };
      this.openAsideRefs.set(trigger, toggleRef);
      trigger.addEventListener('click', toggleRef);
    });
  }
  _unbindTriggers() {
    var _this$openAsideRefs, _this$openAsideRefs2;
    (_this$openAsideRefs = this.openAsideRefs) === null || _this$openAsideRefs === void 0 || _this$openAsideRefs.forEach((listener, trigger) => {
      trigger.removeEventListener('click', listener);
    });
    (_this$openAsideRefs2 = this.openAsideRefs) === null || _this$openAsideRefs2 === void 0 || _this$openAsideRefs2.clear();
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAside();
    }
  }
  openAside() {
    var trigger = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    this.classList.add('aside--open');
    this._syncScrollLockState();
    document.addEventListener('keydown', this.handleKeydownRef);
    this.setAttribute('aria-hidden', 'false');
    this.triggerElement = trigger || null;
    if (this.triggerElement) {
      this.triggerElement.setAttribute('aria-expanded', 'true');
    }
    var firstFocusable = this.panel.querySelector('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    firstFocusable === null || firstFocusable === void 0 || firstFocusable.focus();
    iamotaEventHandler.trigger(['aside:opened', "aside:opened:".concat(this.id)], {
      aside: this,
      trigger
    });
  }

  /**
   * @param {HTMLElement | null} focusTrigger — trigger that initiated close (e.g. second
   *   opener acting as close); falls back to the element that opened the aside.
   */
  closeAside() {
    var _ref;
    var focusTrigger = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    this.classList.remove('aside--open');
    this._syncScrollLockState();
    document.removeEventListener('keydown', this.handleKeydownRef);
    this.setAttribute('aria-hidden', 'true');
    var opener = this.triggerElement;
    var toFocus = (_ref = focusTrigger instanceof Element ? focusTrigger : null) !== null && _ref !== void 0 ? _ref : opener;
    if (opener) {
      opener.setAttribute('aria-expanded', 'false');
    }
    this.triggerElement = null;
    if (toFocus) {
      toFocus.focus();
    }
    iamotaEventHandler.trigger(['aside:closed', "aside:closed:".concat(this.id)], {
      aside: this
    });
  }
}
customElements.define('iam-aside', iamAside);